import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import Layout from "../../components/layout/Layout";
import { api } from "../../api/api";
import { tr } from "../../i18n";
import { useContextUserId } from "../../hooks/useSupportContext";
import { isSuperAdmin } from "../../utils/accessControl";
import "./integrations.css";

const CATEGORY_LABELS = {
  hospitality: "Hotels and hospitality",
  property: "Property and housing",
  office: "Office and coworking",
  calendar: "Calendar and meeting rooms",
  restaurant: "Restaurant and POS",
  transit: "Transport and public transit",
  weather_news: "Weather and news",
  social: "Social media",
  other: "Other",
};

const STATUS = {
  connected: { label: "Connected", kind: "good" },
  not_connected: { label: "Not connected", kind: "none" },
  needs_config: { label: "Needs configuration", kind: "warning" },
  error: { label: "Connection error", kind: "critical" },
  coming_soon: { label: "Coming soon", kind: "soon" },
};

function initials(name) {
  return String(name || "?")
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function IntegrationCard({ item, canManage, busy, onConnect, onDisconnect }) {
  const status = STATUS[item.status] || STATUS.not_connected;
  const soon = item.status === "coming_soon";
  let action = null;
  if (soon) {
    action = null;
  } else if (item.status === "connected" || item.status === "error" || item.status === "needs_config") {
    action = canManage && (
      <Button size="small" variant="outlined" color="inherit" disabled={busy} onClick={() => onDisconnect(item)}>
        {tr("Disconnect")}
      </Button>
    );
  } else if (!item.unlocked) {
    action = <span className="int-note">{tr("Add-on – contact Notiz to activate")}</span>;
  } else if (!item.serverReady) {
    action = <span className="int-note">{tr("Not activated by Notiz yet")}</span>;
  } else if (canManage) {
    action = (
      <Button size="small" variant="contained" disabled={busy} onClick={() => onConnect(item)}>
        {tr("Connect now")}
      </Button>
    );
  }

  return (
    <article className={`int-card${soon ? " int-card--soon" : ""}`}>
      <div className="int-card-head">
        <div className={`int-logo int-logo--${item.category}`} aria-hidden="true">
          {initials(item.name)}
        </div>
        <div className="int-card-title">
          <h3>
            {item.name}
            {item.beta && <span className="int-beta">{tr("Beta")}</span>}
          </h3>
          <span className="int-muted">{tr(CATEGORY_LABELS[item.category] || "Other")}</span>
        </div>
        <span className={`int-status int-status--${status.kind}`}>{tr(status.label)}</span>
      </div>
      <p className="int-desc">{item.description}</p>
      {item.features?.length > 0 && (
        <ul className="int-features">
          {item.features.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      {item.status === "error" && item.lastError && <p className="int-error">{item.lastError}</p>}
      <div className="int-card-foot">
        <span className={`int-price${item.tier === "premium" ? " int-price--premium" : ""}`}>
          {item.tier === "premium" ? tr("Add-on") : tr("Included")}
          {item.priceText ? ` · ${item.priceText}` : ""}
        </span>
        {action}
      </div>
    </article>
  );
}

function Catalog({ contextUserId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [onlyConnected, setOnlyConnected] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    setError("");
    api
      .get(`/integrations/catalog/${contextUserId}`)
      .then((r) => setData(r.data))
      .catch(() => setError(tr("Could not load integrations.")));
  }, [contextUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const replace = (row) =>
    setData((d) => ({ ...d, integrations: d.integrations.map((i) => (i.id === row.id ? row : i)) }));

  const change = async (item, path, okText) => {
    setBusyId(item.id);
    try {
      const r = await api.post(`/integrations/${path}/${contextUserId}`, { integrationId: item.id });
      replace(r.data.integration);
      toast.success(okText);
    } catch (e) {
      toast.error(e?.response?.data?.message || tr("Something went wrong."));
    } finally {
      setBusyId("");
    }
  };

  const items = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.integrations
      .filter((i) => category === "all" || i.category === category)
      .filter((i) => !onlyConnected || i.status === "connected")
      .filter((i) => !q || `${i.name} ${i.description} ${(i.features || []).join(" ")}`.toLowerCase().includes(q))
      // Working ones first, "coming soon" last; otherwise keep the catalog order.
      .sort((a, b) => (a.status === "coming_soon") - (b.status === "coming_soon"));
  }, [data, query, category, onlyConnected]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <div className="int-loading"><CircularProgress size={28} /></div>;

  const usedCategories = data.categories.filter((c) => data.integrations.some((i) => i.category === c));

  return (
    <>
      {!data.organizationId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tr("Choose an organization to connect integrations for it.")}
        </Alert>
      )}
      <div className="int-toolbar">
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tr("Search integrations")}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 240, flex: "1 1 240px", maxWidth: 360 }}
        />
        <ToggleButton
          size="small"
          value="connected"
          selected={onlyConnected}
          onChange={() => setOnlyConnected((v) => !v)}
          sx={{ textTransform: "none" }}
        >
          {tr("Show connected only")}
        </ToggleButton>
      </div>
      <div className="int-chips" role="toolbar" aria-label={tr("Category")}>
        <Chip label={tr("All")} color={category === "all" ? "primary" : "default"} variant={category === "all" ? "filled" : "outlined"} onClick={() => setCategory("all")} />
        {usedCategories.map((c) => (
          <Chip
            key={c}
            label={tr(CATEGORY_LABELS[c] || c)}
            color={category === c ? "primary" : "default"}
            variant={category === c ? "filled" : "outlined"}
            onClick={() => setCategory(c)}
          />
        ))}
      </div>
      {items.length === 0 ? (
        <p className="int-empty">{tr("No integrations match.")}</p>
      ) : (
        <div className="int-grid">
          {items.map((item) => (
            <IntegrationCard
              key={item.id}
              item={item}
              canManage={data.canManage}
              busy={busyId === item.id}
              onConnect={(i) => change(i, "connect", tr("{name} is connected", { name: i.name }))}
              onDisconnect={(i) => change(i, "disconnect", tr("{name} is disconnected", { name: i.name }))}
            />
          ))}
        </div>
      )}
    </>
  );
}

function AdminSettings() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/integrations/admin/settings")
      .then((r) => setRows(r.data.integrations))
      .catch(() => setError(tr("Could not load settings.")));
  }, []);

  const save = async (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      await api.put(`/integrations/admin/settings/${id}`, patch);
    } catch (e) {
      toast.error(tr("Could not save."));
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!rows) return <div className="int-loading"><CircularProgress size={28} /></div>;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <p className="int-muted" style={{ marginTop: 0 }}>
        {tr("Choose which integrations customers see, and what they cost. Premium integrations are unlocked per organization.")}
      </p>
      <Table size="small" className="int-admin">
        <TableHead>
          <TableRow>
            <TableCell>{tr("Integration")}</TableCell>
            <TableCell>{tr("State")}</TableCell>
            <TableCell align="center">{tr("Offered")}</TableCell>
            <TableCell align="center">{tr("Beta")}</TableCell>
            <TableCell>{tr("Price model")}</TableCell>
            <TableCell>{tr("Price text")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.name}</TableCell>
              <TableCell>
                {!r.available ? tr("Coming soon") : r.serverReady ? tr("Working") : tr("Server key missing")}
              </TableCell>
              <TableCell align="center">
                <Switch checked={r.offered} onChange={(e) => save(r.id, { offered: e.target.checked })} inputProps={{ "aria-label": tr("Offered") }} />
              </TableCell>
              <TableCell align="center">
                <Switch checked={r.beta} onChange={(e) => save(r.id, { beta: e.target.checked })} inputProps={{ "aria-label": tr("Beta") }} />
              </TableCell>
              <TableCell>
                <Select size="small" value={r.tier} onChange={(e) => save(r.id, { tier: e.target.value })}>
                  <MenuItem value="included">{tr("Included")}</MenuItem>
                  <MenuItem value="premium">{tr("Add-on")}</MenuItem>
                </Select>
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  defaultValue={r.priceText}
                  placeholder={tr("e.g. 99 kr/month")}
                  onBlur={(e) => e.target.value !== r.priceText && save(r.id, { priceText: e.target.value })}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function Integrations({ user }) {
  const contextUserId = useContextUserId();
  const [tab, setTab] = useState("catalog");
  const admin = isSuperAdmin(user);

  return (
    <Layout title={tr("Integrations (beta)")} user={user}>
      <Box className="int-page">
        <header className="int-hero">
          <h1>{tr("Integrations (beta)")}</h1>
          <p>{tr("Connect the systems you already use and show their information on your screens.")}</p>
        </header>
        {admin && (
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab value="catalog" label={tr("Catalog")} />
            <Tab value="admin" label={tr("Settings")} />
          </Tabs>
        )}
        {tab === "admin" && admin ? <AdminSettings /> : <Catalog contextUserId={contextUserId} />}
      </Box>
    </Layout>
  );
}
